/**
 * Attributes Service
 * Handles all attribute-related API calls
 */

import network from '../../Network/NetworkManager';

const APPURL = require('../../Constants/URLConstants');

/**
 * Get attributes by type and category
 * @param {object} params - Query parameters
 * @returns {Promise<object>} - Response with attributes array
 */
export const getAttributes = async (params = {}) => {
  try {
    const {
      type = 'listings',
      categoryId,
      page = 1,
      perPage = 100,
    } = params;

    const queryParams = {
      type,
      page: page.toString(),
      per_page: perPage.toString(),
    };

    if (categoryId) {
      queryParams.category_id = categoryId.toString();
    }

    const queryString = new URLSearchParams(queryParams).toString();

    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}?${queryString}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching attributes:', error);
    throw error;
  }
};

/**
 * Get grouped attributes by type
 * @param {string} type - Attribute type (listings, accounts, etc.)
 * @returns {Promise<object>} - Response with grouped attributes
 */
export const getGroupedAttributes = async (type = 'listings') => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/grouped?type=${type}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching grouped attributes:', error);
    throw error;
  }
};

/**
 * Get attribute values for an attribute
 * @param {number} attributeId - Attribute ID
 * @returns {Promise<object>} - Response with attribute values
 */
export const getAttributeValues = async (attributeId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}/values`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching attribute values:', error);
    throw error;
  }
};

/**
 * Get attribute groups by type
 * @param {string} type - Attribute group type
 * @returns {Promise<object>} - Response with attribute groups
 */
export const getAttributeGroups = async (type = 'listings') => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attribute_groups}?type=${type}`,
      'GET',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error fetching attribute groups:', error);
    throw error;
  }
};

/**
 * Create a new attribute
 * @param {object} attributeData - Attribute data
 * @returns {Promise<object>} - Response with created attribute
 */
export const createAttribute = async (attributeData) => {
  try {
    const response = await network.networkCall(
      APPURL.URLPaths.attributes,
      'POST',
      JSON.stringify({ attribute: attributeData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error creating attribute:', error);
    throw error;
  }
};

/**
 * Update an attribute
 * @param {number} attributeId - Attribute ID
 * @param {object} attributeData - Updated attribute data
 * @returns {Promise<object>} - Response with updated attribute
 */
export const updateAttribute = async (attributeId, attributeData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}`,
      'PUT',
      JSON.stringify({ attribute: attributeData }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating attribute:', error);
    throw error;
  }
};

/**
 * Delete an attribute
 * @param {number} attributeId - Attribute ID
 * @returns {Promise<object>} - Response
 */
export const deleteAttribute = async (attributeId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting attribute:', error);
    throw error;
  }
};

/**
 * Add attribute value
 * @param {number} attributeId - Attribute ID
 * @param {object} valueData - Value data
 * @returns {Promise<object>} - Response with created value
 */
export const addAttributeValue = async (attributeId, valueData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}/values`,
      'POST',
      JSON.stringify({ attribute: { value: valueData } }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error adding attribute value:', error);
    throw error;
  }
};

/**
 * Update attribute value
 * @param {number} attributeId - Attribute ID
 * @param {number} valueId - Value ID
 * @param {object} valueData - Updated value data
 * @returns {Promise<object>} - Response with updated value
 */
export const updateAttributeValue = async (attributeId, valueId, valueData) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}/values/${valueId}`,
      'PUT',
      JSON.stringify({ attribute: { value: valueData } }),
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error updating attribute value:', error);
    throw error;
  }
};

/**
 * Delete attribute value
 * @param {number} attributeId - Attribute ID
 * @param {number} valueId - Value ID
 * @returns {Promise<object>} - Response
 */
export const deleteAttributeValue = async (attributeId, valueId) => {
  try {
    const response = await network.networkCall(
      `${APPURL.URLPaths.attributes}/${attributeId}/values/${valueId}`,
      'DELETE',
      '',
      '',
      '',
    );

    return response;
  } catch (error) {
    console.error('Error deleting attribute value:', error);
    throw error;
  }
};
